import React, { Dispatch, PropsWithChildren, SetStateAction, useContext, useState } from 'react';

/**
 * TYPE: GiphyContextValue
 * Defines the shape of Giphy context value
 * PROPERTIES:
 * - giphyState: Boolean indicating if Giphy picker is open
 * - setGiphyState: React state setter to update giphyState
 */
export type GiphyContextValue = {
  giphyState: boolean;
  setGiphyState: Dispatch<SetStateAction<boolean>>;
};

/**
 * CREATE CONTEXT
 * Creates GiphyContext with null default value
 * NULL DEFAULT:
 * Indicates context not yet initialized
 * Hook casts to GiphyContextValue (assumes always used within provider)
 */
const GiphyContext = React.createContext<GiphyContextValue | null>(null);

/**
 * Wraps channel UI to provide Giphy state to all child components
 * PROPS:
 * - children: React components that need access to Giphy state
 * STATE:
 * - giphyState: Boolean tracking if Giphy picker is open
 *   Initial value: false (picker closed)
 * EXECUTION FLOW:
 * 1. Component mounts → initializes giphyState as false
 * 2. Provides giphyState and setGiphyState to children
 * 3. When /giphy command detected → setGiphyState(true)
 * 4. GiphyContext consumers re-render with new state
 * 5. Picker UI appears
 * 6. After GIF selected → setGiphyState(false)
 * 7. Picker UI disappears
 * USAGE IN APP:
 * Typically wraps ChannelInner in App.tsx:
 * <GiphyContextProvider>
 *   <ChannelInner ... />
 * </GiphyContextProvider>
 */
export const GiphyContextProvider = (props: PropsWithChildren<unknown>) => {
  /**
   * STATE: giphyState
   * Controls Giphy picker visibility
   * STATES:
   * - false: Picker closed (default)
   * - true: Picker open (user typing /giphy command)
   * UPDATED BY:
   * - Stream Chat's command detection logic
   * - Custom message input components
   * - Giphy picker close handlers
   */
  const [giphyState, setGiphyState] = useState(false);
  
  return (
    <GiphyContext.Provider value={{ giphyState, setGiphyState }}>
      {props.children}
    </GiphyContext.Provider>
  );
};

/**
 * Provides access to Giphy context in components
 * RETURNS:
 * GiphyContextValue with:
 * - giphyState: Current picker visibility state
 * - setGiphyState: Function to open/close picker
 * TYPE CAST:
 * Casts context to GiphyContextValue (assumes always used within provider)
 * Does NOT throw error if used outside provider (unlike other contexts)
 * TYPICAL CONSUMERS:
 * - MessageInput (detects /giphy command)
 * - ChannelInner (adjusts layout for picker)
 * - Custom message components (GIF preview)
 */
export const useGiphyContext = () => {
  return useContext(GiphyContext) as GiphyContextValue;
};
